import React, { useEffect, useState } from "react";
import { api, fmtINR, fmtNum, todayISO, fmtDate } from "../lib/api";
import { Plus, Trash2, PackagePlus } from "lucide-react";
import { toast } from "sonner";

const MUTTON_MODES = [
  { key: "mutton_carcass", label: "Carcass Only", help: "Just meat (no offals)" },
  { key: "mutton_offals", label: "Offals Only", help: "Head, liver, trotters individually priced" },
  { key: "mutton_combined", label: "Combined", help: "Carcass + offals at single combined rate" },
];

export default function Purchases() {
  const [purchases, setPurchases] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [tab, setTab] = useState("chicken"); // 'chicken' | 'mutton'

  // Chicken form
  const [chDate, setChDate] = useState(todayISO());
  const [chKg, setChKg] = useState("");
  const [chRate, setChRate] = useState("");
  const [chSupplier, setChSupplier] = useState("");
  const [chPaid, setChPaid] = useState("");
  const [chPayStatus, setChPayStatus] = useState("paid");

  // Mutton form
  const [mtDate, setMtDate] = useState(todayISO());
  const [mtMode, setMtMode] = useState("mutton_carcass");
  const [mtSupplier, setMtSupplier] = useState("");
  const [mtKg, setMtKg] = useState("");
  const [mtRate, setMtRate] = useState("");
  const [mtPaid, setMtPaid] = useState("");
  const [mtPayStatus, setMtPayStatus] = useState("paid");
  const [mtItems, setMtItems] = useState([
    { label: "Head", kg: "", price_per_kg: "", unit: "piece" },
    { label: "Liver", kg: "", price_per_kg: "", unit: "kg" },
  ]);
  // Combined-mode offals breakdown (informational — no separate prices)
  const [mtBreakdown, setMtBreakdown] = useState([
    { label: "Liver", unit: "kg", qty: "" },
    { label: "Kudal", unit: "kg", qty: "" },
    { label: "Suvarotti", unit: "kg", qty: "" },
    { label: "Head", unit: "piece", qty: "" },
    { label: "Trotters (Paya)", unit: "piece", qty: "" },
    { label: "Brain", unit: "piece", qty: "" },
    { label: "Lungs", unit: "piece", qty: "" },
  ]);

  const load = async () => {
    const res = await api.get("/purchases");
    setPurchases(res.data);
  };

  useEffect(() => {
    load();
    api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
  }, []);

  const saveChicken = async () => {
    const kg = parseFloat(chKg) || 0;
    const rate = parseFloat(chRate) || 0;
    if (!kg || !rate) {
      toast.error("Enter kg and rate");
      return;
    }
    await api.post("/purchases", {
      date: chDate,
      type: "live_chicken",
      supplier: chSupplier,
      paid_amount: parseFloat(chPaid) || (chPayStatus === "paid" ? kg * rate : 0),
      payment_status: chPayStatus,
      items: [
        { label: "Live Chicken", kg, price_per_kg: rate, amount: +(kg * rate).toFixed(2) },
      ],
    });
    toast.success("Chicken purchase saved");
    setChKg("");
    setChRate("");
    setChSupplier("");
    setChPaid("");
    setChPayStatus("paid");
    load();
  };

  const saveMutton = async () => {
    let items = [];
    if (mtMode === "mutton_carcass" || mtMode === "mutton_combined") {
      const kg = parseFloat(mtKg) || 0;
      const rate = parseFloat(mtRate) || 0;
      if (!kg || !rate) {
        toast.error("Enter kg and rate");
        return;
      }
      items.push({
        label: mtMode === "mutton_combined" ? "Carcass + Offals (combined)" : "Carcass",
        kg,
        price_per_kg: rate,
        amount: +(kg * rate).toFixed(2),
      });
    } else {
      // offals only
      items = mtItems
        .filter((it) => parseFloat(it.kg) > 0 && parseFloat(it.price_per_kg) > 0)
        .map((it) => ({
          label: it.label,
          unit: it.unit || "kg",
          kg: parseFloat(it.kg),
          price_per_kg: parseFloat(it.price_per_kg),
          amount: +(parseFloat(it.kg) * parseFloat(it.price_per_kg)).toFixed(2),
        }));
      if (items.length === 0) {
        toast.error("Add at least one offal item");
        return;
      }
    }

    const breakdownPayload =
      mtMode === "mutton_combined"
        ? mtBreakdown
            .filter((b) => parseFloat(b.qty) > 0 && b.label.trim())
            .map((b) => ({
              label: b.label.trim(),
              unit: b.unit,
              qty: parseFloat(b.qty),
            }))
        : [];

    await api.post("/purchases", {
      date: mtDate,
      type: mtMode,
      supplier: mtSupplier,
      paid_amount: parseFloat(mtPaid) || 0,
      payment_status: mtPayStatus,
      items,
      offals_breakdown: breakdownPayload,
    });
    toast.success("Mutton purchase saved");
    setMtKg("");
    setMtRate("");
    setMtSupplier("");
    setMtPaid("");
    setMtPayStatus("paid");
    setMtItems([
      { label: "Head", kg: "", price_per_kg: "", unit: "piece" },
      { label: "Liver", kg: "", price_per_kg: "", unit: "kg" },
    ]);
    setMtBreakdown((b) => b.map((it) => ({ ...it, qty: "" })));
    load();
  };

  const updBreakdownRow = (idx, field, val) => {
    setMtBreakdown((arr) =>
      arr.map((it, i) => (i === idx ? { ...it, [field]: val } : it))
    );
  };
  const addBreakdownRow = () =>
    setMtBreakdown((arr) => [...arr, { label: "", unit: "kg", qty: "" }]);
  const removeBreakdownRow = (idx) =>
    setMtBreakdown((arr) => arr.filter((_, i) => i !== idx));

  const updOffalItem = (idx, field, val) => {
    setMtItems((arr) => arr.map((it, i) => (i === idx ? { ...it, [field]: val } : it)));
  };
  const addOffalRow = () =>
    setMtItems((arr) => [...arr, { label: "", kg: "", price_per_kg: "", unit: "kg" }]);
  const removeOffalRow = (idx) => setMtItems((arr) => arr.filter((_, i) => i !== idx));

  const deletePurchase = async (id) => {
    if (!window.confirm("Delete this purchase?")) return;
    await api.delete(`/purchases/${id}`);
    load();
  };

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto page-enter" data-testid="purchases-page">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
          Raw Material In
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter mt-1">
          Purchases
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Entry forms */}
        <div>
          <div className="flex border-b border-zinc-200 mb-5">
            <button
              onClick={() => setTab("chicken")}
              data-testid="purchase-tab-chicken"
              className={`px-5 py-3 text-sm font-medium uppercase tracking-wider border-b-2 ${
                tab === "chicken"
                  ? "border-sky-600 text-zinc-950"
                  : "border-transparent text-zinc-500 hover:text-zinc-900"
              }`}
            >
              Live Chicken
            </button>
            <button
              onClick={() => setTab("mutton")}
              data-testid="purchase-tab-mutton"
              className={`px-5 py-3 text-sm font-medium uppercase tracking-wider border-b-2 ${
                tab === "mutton"
                  ? "border-sky-600 text-zinc-950"
                  : "border-transparent text-zinc-500 hover:text-zinc-900"
              }`}
            >
              Mutton
            </button>
          </div>
          <datalist id="supplier-datalist">
            {suppliers.map((s) => (
              <option key={s.id} value={s.name} />
            ))}
          </datalist>

          {tab === "chicken" ? (
            <div className="border border-zinc-200 bg-white p-5 space-y-3" data-testid="chicken-form">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500">Date</label>
                <input
                  type="date"
                  value={chDate}
                  onChange={(e) => setChDate(e.target.value)}
                  data-testid="ch-date"
                  className="w-full border border-zinc-300 px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-sky-600"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500">Live weight (kg)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={chKg}
                    onChange={(e) => setChKg(e.target.value)}
                    data-testid="ch-kg"
                    className="w-full border border-zinc-300 px-3 py-2 mt-1 font-mono-num focus:outline-none focus:ring-2 focus:ring-sky-600"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500">Rate ₹/kg</label>
                  <input
                    type="number"
                    step="0.01"
                    value={chRate}
                    onChange={(e) => setChRate(e.target.value)}
                    data-testid="ch-rate"
                    className="w-full border border-zinc-300 px-3 py-2 mt-1 font-mono-num focus:outline-none focus:ring-2 focus:ring-sky-600"
                  />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500">Supplier (optional)</label>
                <input
                  list="supplier-datalist"
                  value={chSupplier}
                  onChange={(e) => setChSupplier(e.target.value)}
                  data-testid="ch-supplier"
                  className="w-full border border-zinc-300 px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-sky-600"
                />
              </div>
              <div className="bg-zinc-50 border border-zinc-200 px-4 py-3 flex justify-between items-baseline">
                <span className="text-xs uppercase tracking-wider text-zinc-500">Total Cost</span>
                <span className="font-display text-2xl font-bold font-mono-num">
                  {fmtINR((parseFloat(chKg) || 0) * (parseFloat(chRate) || 0))}
                </span>
              </div>

              {/* Payment status */}
              <div className="border border-zinc-200 p-3">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
                  Payment Status
                </div>
                <div className="grid grid-cols-3 gap-2 mb-2">
                  {[
                    { k: "paid", label: "Paid" },
                    { k: "partial", label: "Partial" },
                    { k: "credit", label: "Credit" },
                  ].map((o) => (
                    <button
                      key={o.k}
                      onClick={() => setChPayStatus(o.k)}
                      data-testid={`ch-pay-${o.k}`}
                      className={`px-3 py-2 text-xs uppercase tracking-wider border ${
                        chPayStatus === o.k
                          ? "border-sky-600 bg-sky-50 text-sky-700"
                          : "border-zinc-300 text-zinc-700 hover:bg-zinc-50"
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
                {chPayStatus === "partial" && (
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Amount paid now"
                    value={chPaid}
                    onChange={(e) => setChPaid(e.target.value)}
                    data-testid="ch-paid"
                    className="w-full border border-zinc-300 px-3 py-2 font-mono-num text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
                  />
                )}
              </div>

              <button
                onClick={saveChicken}
                data-testid="ch-save"
                className="w-full bg-sky-600 text-white py-3 font-medium uppercase tracking-wider hover:bg-sky-700 flex items-center justify-center gap-2"
              >
                <PackagePlus className="h-4 w-4" />
                Save Chicken Purchase
              </button>
            </div>
          ) : (
            <div className="border border-zinc-200 bg-white p-5 space-y-3" data-testid="mutton-form">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500">Date</label>
                <input
                  type="date"
                  value={mtDate}
                  onChange={(e) => setMtDate(e.target.value)}
                  data-testid="mt-date"
                  className="w-full border border-zinc-300 px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-sky-600"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-2">
                  Mode
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {MUTTON_MODES.map((m) => (
                    <button
                      key={m.key}
                      onClick={() => setMtMode(m.key)}
                      data-testid={`mt-mode-${m.key}`}
                      className={`border px-3 py-2 text-xs uppercase tracking-wider ${
                        mtMode === m.key
                          ? "border-sky-600 bg-sky-50 text-sky-700"
                          : "border-zinc-300 text-zinc-700 hover:bg-zinc-50"
                      }`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
                <div className="text-xs text-zinc-500 mt-2">
                  {MUTTON_MODES.find((m) => m.key === mtMode)?.help}
                </div>
              </div>

              {(mtMode === "mutton_carcass" || mtMode === "mutton_combined") && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-zinc-500">Weight (kg)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={mtKg}
                      onChange={(e) => setMtKg(e.target.value)}
                      data-testid="mt-kg"
                      className="w-full border border-zinc-300 px-3 py-2 mt-1 font-mono-num focus:outline-none focus:ring-2 focus:ring-sky-600"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-zinc-500">Rate ₹/kg</label>
                    <input
                      type="number"
                      step="0.01"
                      value={mtRate}
                      onChange={(e) => setMtRate(e.target.value)}
                      data-testid="mt-rate"
                      className="w-full border border-zinc-300 px-3 py-2 mt-1 font-mono-num focus:outline-none focus:ring-2 focus:ring-sky-600"
                    />
                  </div>
                </div>
              )}

              {mtMode === "mutton_combined" && (
                <div className="border border-amber-200 bg-amber-50 p-3 space-y-2" data-testid="mt-breakdown-section">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-amber-800 font-semibold">
                        Offals Breakdown (for inventory)
                      </div>
                      <div className="text-[11px] text-amber-700 mt-0.5">
                        Track quantities received with the carcass — no extra cost (combined rate).
                      </div>
                    </div>
                    <button
                      onClick={addBreakdownRow}
                      data-testid="mt-breakdown-add"
                      className="text-[10px] uppercase tracking-wider border border-amber-300 bg-white px-2 py-1 hover:bg-amber-100 flex items-center gap-1"
                    >
                      <Plus className="h-3 w-3" /> Row
                    </button>
                  </div>
                  {mtBreakdown.map((b, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center">
                      <input
                        value={b.label}
                        onChange={(e) => updBreakdownRow(i, "label", e.target.value)}
                        data-testid={`mt-bd-label-${i}`}
                        className="col-span-5 border border-amber-200 bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
                      />
                      <input
                        type="number"
                        step={b.unit === "piece" ? "1" : "0.01"}
                        placeholder={b.unit === "piece" ? "pcs" : "kg"}
                        value={b.qty}
                        onChange={(e) => updBreakdownRow(i, "qty", e.target.value)}
                        data-testid={`mt-bd-qty-${i}`}
                        className="col-span-3 border border-amber-200 bg-white px-2 py-1.5 font-mono-num text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
                      />
                      <select
                        value={b.unit}
                        onChange={(e) => updBreakdownRow(i, "unit", e.target.value)}
                        data-testid={`mt-bd-unit-${i}`}
                        className="col-span-3 border border-amber-200 bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
                      >
                        <option value="kg">Kg</option>
                        <option value="piece">Piece</option>
                      </select>
                      <button
                        onClick={() => removeBreakdownRow(i)}
                        className="col-span-1 border border-amber-200 bg-white hover:bg-sky-50 hover:text-sky-600 py-1.5"
                        data-testid={`mt-bd-remove-${i}`}
                      >
                        <Trash2 className="h-3 w-3 mx-auto" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {mtMode === "mutton_offals" && (
                <div className="space-y-2">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Offal Items</div>
                  {mtItems.map((it, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2">
                      <input
                        placeholder="Label"
                        value={it.label}
                        onChange={(e) => updOffalItem(i, "label", e.target.value)}
                        data-testid={`mt-offal-label-${i}`}
                        className="col-span-3 border border-zinc-300 px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
                      />
                      <input
                        type="number"
                        step={it.unit === "piece" ? "1" : "0.01"}
                        placeholder={it.unit === "piece" ? "pcs" : "kg"}
                        value={it.kg}
                        onChange={(e) => updOffalItem(i, "kg", e.target.value)}
                        data-testid={`mt-offal-kg-${i}`}
                        className="col-span-2 border border-zinc-300 px-2 py-2 font-mono-num text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
                      />
                      <select
                        value={it.unit || "kg"}
                        onChange={(e) => updOffalItem(i, "unit", e.target.value)}
                        data-testid={`mt-offal-unit-${i}`}
                        className="col-span-3 border border-zinc-300 px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
                      >
                        <option value="kg">Kg</option>
                        <option value="piece">Piece</option>
                      </select>
                      <input
                        type="number"
                        step="0.01"
                        placeholder={it.unit === "piece" ? "₹/pc" : "₹/kg"}
                        value={it.price_per_kg}
                        onChange={(e) => updOffalItem(i, "price_per_kg", e.target.value)}
                        data-testid={`mt-offal-rate-${i}`}
                        className="col-span-3 border border-zinc-300 px-2 py-2 font-mono-num text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
                      />
                      <button
                        onClick={() => removeOffalRow(i)}
                        className="col-span-1 border border-zinc-300 hover:bg-sky-50 hover:text-sky-600"
                        data-testid={`mt-offal-remove-${i}`}
                      >
                        <Trash2 className="h-4 w-4 mx-auto" />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={addOffalRow}
                    data-testid="mt-offal-add"
                    className="text-xs uppercase tracking-wider text-zinc-700 border border-zinc-300 px-3 py-1.5 hover:bg-zinc-50 flex items-center gap-1"
                  >
                    <Plus className="h-3 w-3" />
                    Add Offal
                  </button>
                </div>
              )}

              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500">Supplier (optional)</label>
                <input
                  list="supplier-datalist"
                  value={mtSupplier}
                  onChange={(e) => setMtSupplier(e.target.value)}
                  data-testid="mt-supplier"
                  className="w-full border border-zinc-300 px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-sky-600"
                />
              </div>

              {/* Payment status */}
              <div className="border border-zinc-200 p-3">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
                  Payment Status
                </div>
                <div className="grid grid-cols-3 gap-2 mb-2">
                  {[
                    { k: "paid", label: "Paid" },
                    { k: "partial", label: "Partial" },
                    { k: "credit", label: "Credit" },
                  ].map((o) => (
                    <button
                      key={o.k}
                      onClick={() => setMtPayStatus(o.k)}
                      data-testid={`mt-pay-${o.k}`}
                      className={`px-3 py-2 text-xs uppercase tracking-wider border ${
                        mtPayStatus === o.k
                          ? "border-sky-600 bg-sky-50 text-sky-700"
                          : "border-zinc-300 text-zinc-700 hover:bg-zinc-50"
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
                {mtPayStatus === "partial" && (
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Amount paid now"
                    value={mtPaid}
                    onChange={(e) => setMtPaid(e.target.value)}
                    data-testid="mt-paid"
                    className="w-full border border-zinc-300 px-3 py-2 font-mono-num text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
                  />
                )}
              </div>

              <button
                onClick={saveMutton}
                data-testid="mt-save"
                className="w-full bg-sky-600 text-white py-3 font-medium uppercase tracking-wider hover:bg-sky-700 flex items-center justify-center gap-2"
              >
                <PackagePlus className="h-4 w-4" />
                Save Mutton Purchase
              </button>
            </div>
          )}
        </div>

        {/* Recent purchases */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
            Recent Purchases
          </div>
          <div className="border border-zinc-200 bg-white" data-testid="purchases-list">
            {purchases.length === 0 ? (
              <div className="p-6 text-center text-sm text-zinc-500">No purchases yet</div>
            ) : (
              purchases.slice(0, 30).map((p) => (
                <div key={p.id} className="border-b border-zinc-200 px-4 py-3" data-testid={`purchase-${p.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs text-zinc-500">{fmtDate(p.date)}</div>
                      <div className="text-sm font-semibold mt-0.5">
                        {p.type.replace("mutton_", "Mutton ").replace("_", " ").replace("live chicken", "Live Chicken")}
                      </div>
                      <div className="text-xs text-zinc-600 mt-1">
                        {p.items.map((it) => it.label).join(", ")}
                        {p.supplier ? ` · ${p.supplier}` : ""}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono-num text-sm">
                        {fmtNum(p.total_kg)} kg
                      </div>
                      <div className="font-display font-bold font-mono-num">
                        {fmtINR(p.total_amount)}
                      </div>
                      <button
                        onClick={() => deletePurchase(p.id)}
                        className="text-[10px] uppercase tracking-wider text-zinc-400 hover:text-sky-600 mt-1"
                        data-testid={`purchase-delete-${p.id}`}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  {(p.offals_breakdown || []).length > 0 && (
                    <div className="mt-2 pt-2 border-t border-dashed border-amber-300 bg-amber-50 -mx-4 px-4 py-2">
                      <div className="text-[10px] uppercase tracking-wider text-amber-800 font-semibold mb-1">
                        Offals Received
                      </div>
                      <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-amber-900 font-mono-num">
                        {(p.offals_breakdown || []).map((b, i) => (
                          <span key={i}>
                            {b.label}: <b>{b.qty} {b.unit === "piece" ? (b.qty === 1 ? "pc" : "pcs") : "kg"}</b>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
